/**
 * Trunnion AI - Chat Session Sidebar
 * Lists past chat sessions grouped by date.
 */

import { Plus, Trash2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { ChatSession } from "@/types/chat.types";

interface ChatSessionSidebarProps {
  sessions: ChatSession[];
  activeSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onNewSession: () => void;
  onDeleteSession: (sessionId: string) => void;
}

function groupByDate(sessions: ChatSession[]): Record<string, ChatSession[]> {
  const groups: Record<string, ChatSession[]> = {};
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now.getTime() - 86400000).toDateString();

  for (const session of sessions) {
    const date = new Date(session.updatedAt).toDateString();
    let label: string;
    if (date === today) label = "Today";
    else if (date === yesterday) label = "Yesterday";
    else
      label = new Date(session.updatedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

    if (!groups[label]) groups[label] = [];
    groups[label].push(session);
  }

  return groups;
}

export function ChatSessionSidebar({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewSession,
  onDeleteSession,
}: ChatSessionSidebarProps) {
  const grouped = groupByDate(sessions);

  return (
    <div className="flex h-full w-64 flex-col border-r border-trunnion-slate-700 bg-trunnion-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-trunnion-slate-700 px-3 py-3">
        <span className="text-sm font-semibold text-white">Sessions</span>
        <Button size="sm" variant="ghost" onClick={onNewSession}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-trunnion-slate-500">
            <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-xs">No sessions yet</p>
          </div>
        ) : (
          Object.entries(grouped).map(([label, items]) => (
            <div key={label}>
              <div className="px-3 py-1.5 text-xs font-medium text-trunnion-slate-500 uppercase tracking-wider">
                {label}
              </div>
              {items.map((session) => (
                <button
                  key={session.id}
                  onClick={() => onSelectSession(session.id)}
                  className={`group flex w-full items-start gap-2 px-3 py-2 text-left transition-colors ${
                    session.id === activeSessionId
                      ? "bg-trunnion-gold-500/10 text-trunnion-gold-500"
                      : "text-trunnion-slate-300 hover:bg-trunnion-slate-800"
                  }`}
                >
                  <MessageSquare className="h-4 w-4 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {session.title ?? "New Chat"}
                    </p>
                    <p className="text-xs text-trunnion-slate-500">
                      {session.messageCount} messages
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSession(session.id);
                    }}
                    className="shrink-0 opacity-0 group-hover:opacity-100 rounded p-0.5 text-trunnion-slate-500 hover:text-red-400 transition-all"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
